import { type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from './Icon'

/** The panel is a reading surface, so its width is the reader's to choose: drag the
 *  left edge (or use the arrow keys on it) and the choice is kept for the next one. */
const STORAGE_KEY = 'seldon.side-panel.width'
const MIN_WIDTH = 420
const DEFAULT_WIDTH = 900
/** Leave the board visible behind the panel however wide it is pulled. */
const EDGE_GAP = 72
/** Typography grows with the panel, but only up to here: past it the lines get long
 *  before the type gets useful. 1rem at the default width, 1.3rem on a wide screen. */
const MAX_SCALE = 1.3

function clampWidth(value: number) {
  const minimum = Math.min(MIN_WIDTH, window.innerWidth)
  const ceiling = Math.max(minimum, window.innerWidth - (window.innerWidth < 600 ? 0 : EDGE_GAP))
  return Math.round(Math.min(ceiling, Math.max(minimum, value)))
}

function storedWidth() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (raw) return clampWidth(Number(raw))
  } catch { /* private mode: the default width is no worse than an error */ }
  return clampWidth(DEFAULT_WIDTH)
}

/**
 * A Notion-style peek: one thing's detail over the board rather than under it.
 *
 * Shared by the job and the node panel so the two read as the same surface — same
 * width, same drag handle, same close behaviour.
 */
export function SidePanel({ label, head, children, onClose }: {
  /** Labels the dialog; the head is expected to carry the same text as a heading. */
  label: string
  head: ReactNode
  children: ReactNode
  onClose: () => void
}) {
  const [width, setWidth] = useState(storedWidth)
  const panel = useRef<HTMLElement>(null)
  const dragging = useRef(false)
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') close.current() }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    panel.current?.focus()
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = previous }
  }, [])

  // A window narrower than the stored width would otherwise push the panel off-screen.
  useEffect(() => {
    const onResize = () => setWidth(current => clampWidth(current))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const remember = useCallback((value: number) => {
    try { window.localStorage.setItem(STORAGE_KEY, String(value)) } catch { /* not worth an error */ }
  }, [])

  const onPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    dragging.current = true
    const handle = event.currentTarget
    handle.setPointerCapture(event.pointerId)
    const move = (moved: PointerEvent) => {
      if (!dragging.current) return
      setWidth(clampWidth(window.innerWidth - moved.clientX))
    }
    const up = () => {
      dragging.current = false
      handle.releasePointerCapture(event.pointerId)
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', up)
      setWidth(current => { remember(current); return current })
    }
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', up)
  }, [remember])

  const onHandleKey = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 120 : 24
    const delta = event.key === 'ArrowLeft' ? step : event.key === 'ArrowRight' ? -step : 0
    if (!delta) return
    event.preventDefault()
    setWidth(current => { const next = clampWidth(current + delta); remember(next); return next })
  }, [remember])

  // The whole panel scales with its width: the screenshots and the video are already
  // fluid, and the type follows them so a wider panel is a bigger read, not just a
  // longer line. Panel-scoped rules size in `em` off this (see styles.css).
  const scale = Math.min(MAX_SCALE, Math.max(0.92, width / DEFAULT_WIDTH))

  return <>
    <div className="job-panel-backdrop" onClick={onClose} aria-hidden="true" />
    <aside className="job-panel" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={panel}
           style={{ width: `${width}px`, fontSize: `${scale}rem` }}>
      <div className="panel-resizer" data-panel-resizer="" onPointerDown={onPointerDown} onKeyDown={onHandleKey}
           role="separator" aria-orientation="vertical" aria-label="Resize the panel"
           aria-valuenow={width} aria-valuemin={Math.min(MIN_WIDTH, window.innerWidth)} aria-valuemax={clampWidth(window.innerWidth)}
           tabIndex={0} title="Drag to resize" />
      <header className="job-panel-head">
        {head}
        <button type="button" className="icon-button" onClick={onClose} aria-label={`Close ${label}`}><Icon name="close" size={16} /></button>
      </header>
      <div className="job-panel-body">{children}</div>
    </aside>
  </>
}
